import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Loader2, Send, Copy } from "lucide-react";

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = [
  { key: "preview", label: "Preview" },
  { key: "content", label: "Content" },
  { key: "recipient", label: "Recipient" },
];

/**
 * InvoiceEmailPreviewModal — Phase 2C (step 2).
 *
 * Renders the branded payment-request email for the given installment via
 * the Stage 6A preview endpoint. SEND uses the existing
 * /installments/{iid}/send route which mints the secure payment link and
 * dispatches the real email through the comms ledger.
 *
 * Tabs: PREVIEW (iframe HTML), CONTENT (subject + summary), RECIPIENT
 * (editable to email/name).
 */
export default function InvoiceEmailPreviewModal({
  open, onClose, installmentId, onSent,
}) {
  const [tab, setTab] = useState("preview");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [toEmail, setToEmail] = useState("");
  const [toName, setToName] = useState("");
  const [sentInfo, setSentInfo] = useState(null);

  const loadPreview = async (overrides = {}) => {
    setLoading(true);
    try {
      const r = await hqApi.post(`/invoices/installments/${installmentId}/preview`, {
        to_email: overrides.to_email ?? toEmail ?? undefined,
        to_name: overrides.to_name ?? toName ?? undefined,
      });
      setData(r.data);
      if (r.data.to_email && !toEmail) setToEmail(r.data.to_email);
      if (r.data.to_name && !toName) setToName(r.data.to_name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not render preview");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !installmentId) return;
    setTab("preview"); setToEmail(""); setToName(""); setSentInfo(null); setData(null);
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, installmentId]);

  const send = async () => {
    if (!toEmail) return toast.error("Recipient email required");
    setSending(true);
    try {
      const r = await hqApi.post(`/invoices/installments/${installmentId}/send`);
      const status = r.data.email_status || "sent";
      setSentInfo(r.data);
      if (status === "sent" || status === "duplicate_suppressed") toast.success(`Email ${status} · ${r.data.to_email || toEmail}`);
      else if (status === "no_recipient") toast.error("No recipient on file");
      else toast.message(`Send status: ${status}`);
      onSent?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!open) return null;

  const amountDue = data?.installment?.amount_minor;
  const invoiceTotal = data?.invoice?.total_amount_minor;
  const paid = data?.invoice?.amount_paid_minor;
  const remainingAfter = invoiceTotal != null ? (invoiceTotal - (paid || 0) - (amountDue || 0)) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-invoice-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Payment request</div>
            <h2 className="font-black text-lg mt-1">
              {data ? `${data.invoice.invoice_number} · ${data.installment.label}` : "Loading…"}
            </h2>
          </div>
          <button data-testid="hq-invoice-preview-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]">
            <X size={16}/>
          </button>
        </div>

        <div className="flex gap-1 px-6 py-3 border-b border-[#EAEAEA] bg-[#F6F5F2]">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`hq-invoice-preview-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}>
              {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3 text-[11px] text-[#4A4A4A]">
            {data && (
              <span data-testid="hq-invoice-preview-amount">
                Due now: <strong>{money(amountDue, data.installment.currency)}</strong>
              </span>
            )}
            {data?.provider && (
              <span className="px-2 py-0.5 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] font-black text-[9px] tracking-[0.16em] uppercase text-[#8A4A00]">
                {data.provider}
              </span>
            )}
          </div>
        </div>

        {loading || !data ? (
          <div className="p-10 flex items-center justify-center"><Loader2 size={18} className="animate-spin"/></div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            {tab === "preview" && (
              <div className="p-4">
                <iframe
                  title="Invoice payment email"
                  data-testid="hq-invoice-preview-iframe"
                  srcDoc={data.html}
                  className="w-full h-[560px] border border-[#EAEAEA] rounded-xl bg-white"
                />
                {data.is_preview_placeholder && (
                  <div className="mt-2 text-[11px] text-[#8A6D00]">
                    Preview shows a placeholder link. The real secure payment URL is generated when you press SEND.
                  </div>
                )}
              </div>
            )}
            {tab === "content" && (
              <div className="p-6 space-y-3 text-sm">
                <ReadOnly label="Subject" value={data.subject}/>
                <div className="grid grid-cols-2 gap-3">
                  <ReadOnly label="Invoice" value={data.invoice.invoice_number}/>
                  <ReadOnly label="Milestone" value={data.installment.label}/>
                  <ReadOnly label="Amount due now" value={money(amountDue, data.installment.currency)}/>
                  <ReadOnly label="Invoice total" value={money(invoiceTotal, data.installment.currency)}/>
                  <ReadOnly label="Already paid" value={money(paid, data.installment.currency)}/>
                  <ReadOnly label="Remaining after this payment"
                    value={remainingAfter != null ? money(Math.max(remainingAfter, 0), data.installment.currency) : "—"}/>
                </div>
              </div>
            )}
            {tab === "recipient" && (
              <div className="p-6 space-y-3 text-sm">
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">To name</div>
                  <input data-testid="hq-invoice-preview-to-name" value={toName} onChange={(e) => setToName(e.target.value)} className="w-full px-3 py-2 rounded-lg border"/>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">To email</div>
                  <input data-testid="hq-invoice-preview-to-email" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border"/>
                </div>
                <p className="text-[11px] text-[#8A8A8A]">Nothing is sent until you press the SEND button below.</p>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex items-center justify-between gap-2">
          <div className="text-[11px] text-[#8A8A8A]">
            {sentInfo?.public_url && (
              <button
                data-testid="hq-invoice-preview-copy"
                onClick={() => { navigator.clipboard?.writeText(sentInfo.public_url); toast.success("Payment link copied"); }}
                className="inline-flex items-center gap-1 text-[#FF6A00] hover:underline"
              >
                <Copy size={12}/> Copy payment link
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="hq-invoice-preview-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
              Close
            </button>
            <button
              data-testid="hq-invoice-preview-send"
              onClick={send}
              disabled={sending || !data || !toEmail}
              className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${!sending && data && toEmail ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"}`}
            >
              {sending ? <Loader2 size={12} className="inline animate-spin mr-1"/> : <Send size={12} className="inline mr-1"/>}
              Send email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReadOnly({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      <div className="px-3 py-2 rounded-lg bg-[#F6F5F2] font-bold">{value || "—"}</div>
    </div>
  );
}
